/**
 * Rogue Dealer API — the single source of API types for this app.
 *
 * Mirrors the Rogue backend DTOs (rogue/backend). Endpoints the backend does not expose yet
 * are marked `[planned]` here and in docs/API_CONTRACT.md; screens that depend on them show an
 * honest "not available" state against the real backend and work fully in mock mode.
 *
 * Money is integer cents. Timestamps are ISO-8601 strings.
 */

// ── enums (identical to backend) ────────────────────────────────────────────
export const OPPORTUNITY_STAGES = [
  "NEW",
  "IDENTIFYING",
  "ENRICHING",
  "VALUED",
  "QUALIFYING",
  "ENGAGED",
  "OFFER_SENT",
  "NEGOTIATING",
  "OFFER_ACCEPTED",
  "APPOINTMENT_SET",
  "APPOINTMENT_CONFIRMED",
  "SHOWED",
  "APPRAISED",
  "PURCHASED",
] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];

export const OPPORTUNITY_STATUSES = ["OPEN", "DISQUALIFIED", "OPTED_OUT", "UNRESPONSIVE", "LOST", "EXPIRED", "DUPLICATE"] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];

/**
 * Where a lead came from. Rogue's answer when a seller asks how we got their number depends on it:
 * KBB_ICO, the KBB website (a KBB partner); CRM, the store itself; FACEBOOK, a colleague they shared it with.
 */
export const OPPORTUNITY_SOURCES = ["KBB_ICO", "CRM", "FACEBOOK"] as const;
export type OpportunitySource = (typeof OPPORTUNITY_SOURCES)[number];

export const CONVERSATION_AI_STATUSES = ["IDLE", "QUEUED", "PROCESSING", "FAILED"] as const;
export type ConversationAiStatus = (typeof CONVERSATION_AI_STATUSES)[number];

/** Who is driving the conversation. [planned] on the backend; absent means ROGUE. */
export type ConversationControl = "ROGUE" | "HUMAN";

export type MessageAiStatus = "PENDING" | "PROCESSING" | "PROCESSED" | "FAILED" | "SKIPPED";
export type DeliveryStatus = "NOT_SENT" | "QUEUED" | "SENT" | "DELIVERED" | "READ" | "FAILED";
export type MessageDirection = "INBOUND" | "OUTBOUND";
export type MessageAuthorType = "SELLER" | "AI" | "USER" | "SYSTEM";

export type AppointmentStatus = "PROPOSED" | "SCHEDULED" | "CONFIRMED" | "SHOWED" | "NO_SHOW" | "CANCELLED";
export type OfferStatus = "RECOMMENDED" | "PENDING_APPROVAL" | "APPROVED" | "BLOCKED" | "SENT" | "ACCEPTED" | "DECLINED" | "WITHDRAWN";
export type RecordSource = "AI" | "USER" | "SYSTEM";

export type VehicleCondition = "EXCELLENT" | "GOOD" | "FAIR" | "POOR" | "UNKNOWN";
export type TitleStatus = "CLEAN" | "SALVAGE" | "REBUILT" | "LIEN" | "MISSING" | "UNKNOWN";
export type OwnershipStatus = "OWNED_OUTRIGHT" | "FINANCED" | "LEASED" | "UNKNOWN";

export type OrgRole = "OWNER" | "ADMIN" | "GROUP_MANAGER";
export type StoreRole = "STORE_MANAGER" | "BUYER" | "BDC" | "APPRAISER" | "VIEWER";

/** Backend permission names (rogue/backend/src/lib/auth/permissions.ts). */
export type BackendPermission =
  | "store:read"
  | "store:manage"
  | "members:manage"
  | "opportunity:read"
  | "opportunity:write"
  | "opportunity:assign"
  | "conversation:read"
  | "message:send"
  | "valuation:write"
  | "valuation:approve"
  | "offer:create"
  | "offer:approve"
  | "offer:send"
  | "appointment:write"
  | "purchase:write"
  | "ai:rerun";

// ── session / tenancy ───────────────────────────────────────────────────────
export interface StoreAccess {
  storeId: string;
  storeName: string;
  role: StoreRole | null;
  via: "STORE_MEMBERSHIP" | "ORGANIZATION_ROLE";
  permissions: BackendPermission[];
}

/** GET /v1/me */
export interface Me {
  user: { id: string; name: string; email: string };
  organization: { id: string; name: string; slug: string };
  organizationRole: OrgRole | null;
  storeMemberships: Array<{ storeId: string; storeName: string; role: StoreRole }>;
  accessibleStores: StoreAccess[];
  activeStore: { storeId: string; storeName: string; role: StoreRole | null } | null;
  /** A Rogue admin working this dealership's leads (opened from the admin console). Absent on older servers. */
  actingAdmin?: { id: string; name: string } | null;
}

/** GET /api/auth/organization/list (Better Auth organization plugin) */
export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
}

export interface StoreSummary {
  id: string;
  name: string;
  slug: string;
  timezone: string;
}

/** GET /v1/stores/:id */
/** SMS providers. A store has a number with each it uses; an opportunity texts through one. */
export const SMS_PROVIDERS = ["TWILIO", "INFOBIP", "TELNYX"] as const;
export type SmsProvider = (typeof SMS_PROVIDERS)[number];

/** A store number sellers can be texted from. */
export interface SmsChannelOption {
  provider: SmsProvider;
  label: string;
  number: string | null;
}

export interface StoreDetail extends StoreSummary {
  /** The store's Twilio number. */
  phoneNumber: string | null;
  infobipNumber?: string | null;
  telnyxNumber?: string | null;
  /** How the store can text sellers (set up by Rogue). Absent on older servers. */
  smsChannels?: SmsChannelOption[];
  /** New leads text through this one unless chosen otherwise. */
  defaultSmsProvider?: SmsProvider;
  /** One line ({{store_address}}). Absent on older servers. */
  address?: string | null;
  policy: StorePolicy;
  /** Read-only: max offers come from these offer API integrations (set up by Rogue). Absent on older servers. */
  offerApi?: { active: boolean; integrations: string[] };
}

export interface StorePolicy {
  offers: { maxPercentOfValuation: number; incrementCents: number; autoApproveMaxCents: number; valuationMaxAgeDays: number };
  messaging: { aiAutoReply: boolean; maxResponseChars: number };
  appointments: {
    aiCanSchedule: boolean;
    minLeadMinutes: number;
    maxDaysAhead: number;
    /** Working hours per weekday (store time). Absent on older servers. */
    hours?: Record<"mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun", { open: boolean; start: string; end: string }>;
    /** Length of a bookable slot, minutes. */
    slotMinutes?: number;
    /** The store's note for sellers ({{note}}). */
    note?: string;
    /** Who sellers ask for on arrival ({{ask_for}}). */
    askFor?: string;
    /** The text sent when staff book or reschedule ({{variables}}). */
    messageTemplate?: string;
  };
  /** Rogue texts new leads first. Absent on older servers. */
  negotiation?: {
    /** Required before an offer besides year/make/model: mileage · vin · title_status · condition · photos. */
    requiredInfo: string[];
    /** Seller's price more than this % above the max offer: Rogue opens at the max. */
    openAtCeilingGapPercent: number;
    /** More than this % above it (or unknown): Rogue asks the seller's price first, once. */
    askPriceGapPercent: number;
    /** Otherwise Rogue opens this far below the max. */
    openingBelowCeilingCents: number;
    maxStepCents: number;
    handoffGapCents: number;
  };
  handoff?: { assignment: "ROUND_ROBIN" | "STORE_MANAGER" | "NONE" };
  firstContact?: { enabled: boolean; delayMinutes: number; sendWindow: { start: string; end: string }; maxPerMinute: number };
}

export interface StoreMember {
  userId: string;
  name: string;
  email: string;
  role: StoreRole;
}

// ── domain records ──────────────────────────────────────────────────────────
export interface Seller {
  id: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  email: string | null;
  phoneVerified: boolean;
  preferredChannel: "SMS" | "EMAIL" | "PHONE";
  optedOutAt: string | null;
}

export interface Vehicle {
  id: string;
  vin: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  mileage: number | null;
  /** When the seller confirmed the mileage; null = from the lead, possibly stale. */
  mileageConfirmedAt?: string | null;
  condition: VehicleCondition;
  titleStatus: TitleStatus;
  ownershipStatus: OwnershipStatus;
  /** Decoded from the VIN (absent on older servers). */
  engine?: string | null;
  transmission?: string | null;
  drivetrain?: string | null;
  bodyStyle?: string | null;
  exteriorColor?: string | null;
  interiorColor?: string | null;
  vinCheck?: VinCheck | null;
  /** Where the colors came from (absent on older servers). */
  sourceData?: { colorSource?: { exterior?: ColorSource; interior?: ColorSource } } | null;
}

/** DECODER: the VIN decoder · LEAD: the import, lead API or manual entry · SELLER: the seller's words or photos · STAFF: the team. */
export type ColorSource = "DECODER" | "LEAD" | "SELLER" | "STAFF";

/**
 * The backend's VIN check. INVALID: not a real VIN (never stored as `vin`). MISMATCH: decodes to
 * another year/make/model than on file (Rogue verifies with the seller; no offer until settled).
 */
export interface VinCheck {
  vin: string;
  status: "PENDING" | "VERIFIED" | "MISMATCH" | "INVALID" | "UNAVAILABLE";
  source: "SELLER" | "LEAD" | "STAFF";
  reason?: string | null;
  decoded?: { year: number | null; make: string | null; model: string | null; trim: string | null } | null;
  mismatches?: Array<{ field: "year" | "make" | "model"; onFile: string; decoded: string }>;
  provider?: string | null;
  checkedAt: string;
}

export type VehiclePatch = Partial<Omit<Vehicle, "id" | "vinCheck" | "sourceData" | "engine" | "transmission" | "drivetrain" | "bodyStyle">>;

/** Backend-validated qualification facts (partly extracted by Rogue). */
export interface Qualification {
  sellerExpectation?: { amountCents: number | null; note: string | null } | null;
  availability?: string | null;
  sellerIntent?: string | null;
  objections?: string[];
  missingInformation?: string[];
  summary?: string | null;
  needsHumanReview?: boolean;
  updatedByAiRunId?: string | null;
}

/** One reason behind the Rogue Score (backend-computed; largest effect first). */
export interface ScoreFactor {
  key?: string;
  label: string;
  impact: "positive" | "negative" | "neutral";
  /** Points this factor adds to or removes from the score (absent on older servers). */
  points?: number;
}

/** Operational next step computed by the backend for a record. [planned] */
export type NextActionKind =
  | "APPROVE_OFFER"
  | "RAISE_OFFER"
  | "FOLLOW_UP"
  | "BOOK_APPOINTMENT"
  | "CONFIRM_APPOINTMENT"
  | "REVIEW_DRAFT"
  | "RETRY_AI"
  | "TAKE_OVER"
  | "SET_OFFER"
  | "HANDOFF"
  | "START_CONVERSATION"
  | "NONE";

export interface NextAction {
  kind: NextActionKind;
  label: string;
  /** Business-safe reasons from the backend ("Why Rogue"). Never model reasoning. */
  reasons?: string[];
}

/** GET /v1/opportunities/:id → opportunity (backend OpportunityDto + [planned] enrichments). */
/** POST /v1/vin/decode: a VIN checked and decoded before the lead exists ("Add lead", step 1). */
export interface VinLookup {
  vin: string;
  /** DECODED · INVALID (not a VIN: fix it) · UNRECOGNIZED / UNAVAILABLE (enter the details by hand). */
  status: "DECODED" | "INVALID" | "UNRECOGNIZED" | "UNAVAILABLE";
  reason: string | null;
  vehicle: {
    year: number | null;
    make: string | null;
    model: string | null;
    trim: string | null;
    engine: string | null;
    transmission: string | null;
    drivetrain: string | null;
    bodyStyle: string | null;
    exteriorColor: string | null;
    interiorColor: string | null;
  } | null;
  provider: string | null;
  /** Leads that already have this VIN, in stores the user can see (newest first). */
  existing: Array<{ opportunityId: string; storeId: string; storeName: string | null; stage: OpportunityStage; status: OpportunityStatus; createdAt: string }>;
}

/** POST /v1/opportunities: a lead entered by hand (the same intake as imports). */
export interface NewLeadInput {
  storeId: string;
  source: OpportunitySource;
  sourceMetadata?: Record<string, unknown>;
  seller: { firstName?: string | null; lastName?: string | null; phone?: string | null; email?: string | null; postalCode?: string | null };
  vehicle: VehiclePatch;
  /** Which of the store's numbers texts the seller (default: the store's). */
  smsProvider?: SmsProvider;
}

export interface Opportunity {
  id: string;
  organizationId: string;
  storeId: string;
  sellerId: string;
  vehicleId: string | null;
  source: OpportunitySource;
  sourceId: string | null;
  sourceMetadata: Record<string, unknown>;
  stage: OpportunityStage;
  status: OpportunityStatus;
  statusReason: string | null;
  /** How Rogue texts the seller (null: the store's default). */
  smsProvider?: SmsProvider | null;
  closedAt: string | null;
  score: number | null;
  scoreFactors: Record<string, number | string | boolean | null> | ScoreFactor[];
  qualification: Qualification;
  assignedUserId: string | null;
  /** The most Rogue may offer (API valuation or set by staff). Rogue opens below it. */
  offerCeiling?: { amountCents: number; source: "API" | "MANUAL" | null; setAt: string | null } | null;
  /** Rogue collected everything and asked the team for a max offer. */
  offerRequestedAt?: string | null;
  /** Rogue handed the conversation to a person. */
  handoff?: { reason: HandoffReason; at: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export type HandoffReason = "SMALL_GAP" | "DEAD_END" | "AGREEMENT" | "ESCALATED";

/**
 * Inbox row. The current backend returns the bare Opportunity; the embedded summaries are
 * [planned] (`GET /v1/opportunities?expand=...`). Every embedded field is nullable so rows render
 * with what the server provides.
 */
export interface OpportunityListItem extends Opportunity {
  storeName?: string | null;
  seller?: Pick<Seller, "id" | "firstName" | "lastName" | "phoneVerified"> | null;
  vehicle?: Pick<Vehicle, "id" | "year" | "make" | "model" | "trim" | "mileage" | "vin"> | null;
  assignedUser?: { id: string; name: string } | null;
  currentOffer?: { id: string; amountCents: number; status: OfferStatus } | null;
  recommendedOfferCents?: number | null;
  nextAppointment?: { id: string; startsAt: string; status: AppointmentStatus } | null;
  conversationId?: string | null;
  aiStatus?: ConversationAiStatus | null;
  control?: ConversationControl | null;
  distanceMiles?: number | null;
  lastActivityAt?: string | null;
  nextAction?: NextAction | null;
  /** Cover photo (signed, short-lived URL). */
  featuredImage?: { id: string; url: string } | null;
  /** Rogue's first text is waiting for the store's texting hours: when it goes out. */
  firstContactAt?: string | null;
  /** Rogue's next message held for texting hours (first text or opening offer). */
  scheduledSend?: { kind: "FIRST_CONTACT" | "OFFER"; at: string } | null;
  imageCount?: number;
}

/** GET /v1/opportunities/:id/images — vehicle photos (dealer uploads and seller MMS). */
export interface OpportunityImage {
  id: string;
  opportunityId: string;
  /** Signed, short-lived URL: never store it. */
  url: string;
  contentType: string;
  sizeBytes: number;
  source: "AI" | "USER" | "SYSTEM";
  description: string | null;
  isFeatured: boolean;
  featuredBy: "AI" | "USER" | "SYSTEM" | null;
  sourceMessageId: string | null;
  createdByUserId: string | null;
  createdAt: string;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  /** [planned] total count for the current filters. */
  total?: number | null;
  /** Conversations queue: lost / opted-out ones the current view leaves out (first page only). */
  hidden?: number;
}

/** GET /v1/opportunities/:id */
export interface OpportunityDetail {
  opportunity: OpportunityListItem;
  seller: Seller | null;
  vehicle: Vehicle | null;
  conversation: Conversation | null;
  /** Where the offer stands. Absent on older servers. */
  negotiation?: NegotiationSummary;
  /** Set once the vehicle is bought. Absent on older servers. */
  purchase?: OpportunityPurchase | null;
  /** How Rogue texts the seller, and the store's other numbers. Absent on older servers. */
  smsChannel?: (SmsChannelOption & { chosen: boolean; options: SmsChannelOption[] }) | null;
}

/** The purchase on an opportunity's detail: price, when it was bought and recorded, and by whom. */
export interface OpportunityPurchase {
  id: string;
  amountCents: number;
  currency: string;
  purchasedAt: string;
  recordedAt: string;
  recordedBy: { id: string; name: string | null } | null;
  offerId: string | null;
  offerAmountCents: number | null;
  notes: string | null;
}

export type NegotiationStatus = "COLLECTING_INFO" | "VALUING" | "AWAITING_MAX_OFFER" | "NEGOTIATING" | "HANDED_OFF" | "AGREED";

export interface NegotiationSummary {
  status: NegotiationStatus;
  /** What Rogue still collects before an offer (COLLECTING_INFO). */
  missingInfo: string[];
  rounds: number;
  lastOfferCents: number | null;
  /** The most Rogue can offer on its next turn; null = it is at its max (or no max yet). */
  nextMaxCents: number | null;
  exhausted: boolean;
  /** The seller's current price: their answer to Rogue's question when it asked, else the lead's asking price. */
  sellerAskCents: number | null;
  /** The lead's asking price, and the seller's own target once Rogue asked (kept apart). Absent on older servers. */
  leadAskCents?: number | null;
  sellerTargetCents?: number | null;
  sellerPriceAskedAt?: string | null;
  /** Rogue will ask what the seller hopes to get before any offer (price far above the max, or unknown). */
  askingSellerPrice?: boolean;
  valuation: {
    status: "PENDING" | "COMPLETED" | "FAILED";
    provider: string;
    averageCents: number | null;
    /** OFFER: a firm buyer offer (e.g. AutoNation) used as the max offer as-is. Absent on older servers. */
    kind?: "OFFER" | "VALUATION";
    expiresAt?: string | null;
    /** Why the API offer failed or was declined. */
    error?: string | null;
    createdAt: string;
  } | null;
}

export interface Conversation {
  id: string;
  organizationId: string;
  storeId: string;
  opportunityId: string;
  channel: "SMS";
  provider: "TWILIO";
  aiStatus: ConversationAiStatus;
  aiStatusLabel: string | null;
  revision: number;
  activeAiRunId: string | null;
  lastMessageAt: string | null;
  /** [planned] human takeover. Absent = ROGUE. */
  control?: ConversationControl;
  controlledBy?: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

/** [planned] GET /v1/conversations list row. */
export interface ConversationListItem extends Conversation {
  opportunityStatus: OpportunityStatus;
  /** The seller opted out of texts (maybe on another lead): nothing can be sent to them. */
  sellerOptedOut?: boolean;
  seller: Pick<Seller, "id" | "firstName" | "lastName"> | null;
  vehicle: Pick<Vehicle, "year" | "make" | "model" | "trim"> | null;
  lastMessage: { body: string; direction: MessageDirection; authorType: MessageAuthorType; createdAt: string } | null;
  unansweredCount: number;
  /** The latest reply that was sent (not a held draft) never reached the seller. */
  deliveryFailed: boolean;
}

export interface Message {
  id: string;
  conversationId: string;
  direction: MessageDirection;
  authorType: MessageAuthorType;
  authorUserId: string | null;
  body: string;
  media: Array<{ url: string; contentType: string | null }>;
  aiStatus: MessageAiStatus;
  deliveryStatus: DeliveryStatus;
  revision: number | null;
  aiRunId: string | null;
  errorCode: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A Rogue run, as the mock backend tracks it for conversation status. The run log itself is admin-only (admin-web). */
export interface AiRun {
  id: string;
  status: "QUEUED" | "PROCESSING" | "COMPLETED" | "FAILED" | "STALE" | "CANCELLED";
  trigger: "INBOUND_MESSAGE" | "STALE_RERUN" | "MANUAL" | "FIRST_CONTACT";
  /** FIRST_CONTACT runs waiting for their slot (store texting hours, pacing). */
  scheduledFor?: string | null;
  revision: number;
  model: string | null;
  provider: string | null;
  promptVersion: string | null;
  inputMessageIds: string[];
  /** Concise business-safe audit reason from the AI result. Never chain-of-thought. */
  reason: string | null;
  action: string | null;
  outcome: Record<string, unknown> | null;
  error: { code: string; message: string } | null;
  durationMs: number | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface Valuation {
  id: string;
  opportunityId: string;
  provider: string;
  status: "PENDING" | "COMPLETED" | "FAILED";
  lowCents: number | null;
  averageCents: number | null;
  highCents: number | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  rawData: Record<string, unknown>;
  createdAt: string;
}

export interface OfferPolicyCheck {
  valuationId: string | null;
  valuationAverageCents: number | null;
  maxAllowedCents: number | null;
  incrementCents: number;
  requiresApproval: boolean;
  violations: string[];
  checkedAt: string;
}

export interface Offer {
  id: string;
  opportunityId: string;
  storeId: string;
  valuationId: string | null;
  amountCents: number;
  status: OfferStatus;
  source: RecordSource;
  aiRunId: string | null;
  rationale: string | null;
  policyCheck: OfferPolicyCheck;
  requiresApproval: boolean;
  approvedAt: string | null;
  sentAt: string | null;
  createdAt: string;
}

export interface Appointment {
  id: string;
  opportunityId: string;
  storeId: string;
  sellerId: string;
  status: AppointmentStatus;
  source: RecordSource;
  startsAt: string;
  endsAt: string | null;
  notes: string | null;
  aiRunId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** [planned] GET /v1/appointments row. */
export interface AppointmentListItem extends Appointment {
  storeName: string | null;
  seller: Pick<Seller, "id" | "firstName" | "lastName"> | null;
  vehicle: Pick<Vehicle, "year" | "make" | "model" | "trim"> | null;
  assignedUser: { id: string; name: string } | null;
  opportunitySource: OpportunitySource | null;
  conversationId: string | null;
}

export interface Purchase {
  id: string;
  opportunityId: string;
  storeId: string;
  vehicleId: string | null;
  offerId: string | null;
  amountCents: number;
  purchasedAt: string;
  notes: string | null;
  createdAt: string;
}

/** [planned] GET /v1/purchases row. */
export interface PurchaseListItem extends Purchase {
  storeName: string | null;
  seller: Pick<Seller, "id" | "firstName" | "lastName"> | null;
  vehicle: Pick<Vehicle, "year" | "make" | "model" | "trim" | "mileage"> | null;
  source: OpportunitySource | null;
  buyer: { id: string; name: string } | null;
  cycleTimeDays: number | null;
  inventoryStatus: "ACQUIRED" | "IN_RECON" | "FRONTLINE" | "SOLD_RETAIL" | "WHOLESALED" | null;
}

/** [planned] GET /v1/opportunities/:id/activity */
export interface ActivityEvent {
  id: string;
  at: string;
  actor: { type: "ROGUE" | "USER" | "SELLER" | "SYSTEM"; name: string | null };
  source: string;
  description: string;
  kind: "created" | "message" | "update" | "valuation" | "offer" | "approval" | "appointment" | "purchase" | "ai";
}

// ── overview / analytics [planned] ──────────────────────────────────────────
export type DateRange = "7d" | "30d" | "90d";

export interface KpiValue {
  value: number | null;
  /** Only present when the backend computes a real comparison. Never fabricated. */
  previous?: number | null;
}

export interface OverviewSummary {
  range: DateRange;
  kpis: {
    newOpportunities: KpiValue;
    qualified: KpiValue;
    appointments: KpiValue;
    purchased: KpiValue;
    costPerPurchasedVehicleCents: KpiValue;
  };
  rogue: {
    activeConversations: number | null;
    offersAwaitingResponse: number | null;
    leadsRequalified: number | null;
    appointmentsToday: number | null;
    approvalsNeeded: number | null;
  };
  priority: OpportunityListItem[];
  attention: {
    approvals: OpportunityListItem[];
    hotSellers: OpportunityListItem[];
    appointmentsToday: AppointmentListItem[];
    expiringOffers: OpportunityListItem[];
  };
}

export interface SourceMetrics {
  source: OpportunitySource;
  connection: "CONNECTED" | "NOT_CONNECTED" | "ERROR" | "MANUAL";
  leads: number | null;
  qualified: number | null;
  appointments: number | null;
  purchases: number | null;
  purchaseRate: number | null;
  costCents: number | null;
}

export interface FunnelStep {
  key: "leads" | "contacted" | "engaged" | "qualified" | "offer" | "appointment" | "show" | "purchased";
  label: string;
  count: number | null;
}

export interface AnalyticsSummary {
  range: DateRange;
  funnel: FunnelStep[];
  metrics: {
    leadVolume: number | null;
    contactRate: number | null;
    responseRate: number | null;
    qualificationRate: number | null;
    appointmentRate: number | null;
    showRate: number | null;
    purchaseRate: number | null;
    costPerPurchasedVehicleCents: number | null;
    medianCycleTimeDays: number | null;
    averageOfferGapCents: number | null;
    grossOpportunityCents: number | null;
  };
}

/** [planned] GET /v1/search?q= */
export interface SearchResults {
  opportunities: Array<{ id: string; title: string; subtitle: string }>;
  conversations: Array<{ id: string; title: string; subtitle: string }>;
  sellers: Array<{ id: string; opportunityId: string | null; title: string; subtitle: string }>;
  vehicles: Array<{ id: string; opportunityId: string | null; title: string; subtitle: string }>;
  appointments: Array<{ id: string; opportunityId: string; title: string; subtitle: string }>;
}

// ── realtime ────────────────────────────────────────────────────────────────
export const REALTIME_EVENT_TYPES = [
  "message.created",
  "message.updated",
  "conversation.ai_status_changed",
  "opportunity.updated",
  "appointment.updated",
  "purchase.updated",
  "lead_import.updated",
] as const;
export type RealtimeEventType = (typeof REALTIME_EVENT_TYPES)[number];

/** SSE envelope sent by GET /v1/events. */
export interface RealtimeEnvelope<T = Record<string, unknown>> {
  id: string;
  type: RealtimeEventType;
  organizationId: string;
  storeId: string | null;
  aggregateType: string;
  aggregateId: string;
  occurredAt: string;
  data: T;
}

export interface AiStatusChangedData {
  conversationId: string;
  opportunityId: string;
  aiStatus: ConversationAiStatus;
  previousAiStatus: ConversationAiStatus;
  revision: number;
  activeAiRunId: string | null;
  control?: ConversationControl;
}

// ── errors ──────────────────────────────────────────────────────────────────
/** Backend error body: { error: { code, message, details?, requestId } } */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown; requestId?: string };
}

// ── lead imports ───────────────────────────────────────────────────────────
export const LEAD_IMPORT_STATUSES = ["DRAFT", "QUEUED", "PROCESSING", "COMPLETED", "FAILED", "CANCELLED"] as const;
export type LeadImportStatus = (typeof LEAD_IMPORT_STATUSES)[number];
export const LEAD_ROW_STATUSES = ["PENDING", "CREATED", "DUPLICATE", "FAILED", "SKIPPED"] as const;
export type LeadRowStatus = (typeof LEAD_ROW_STATUSES)[number];

/** A field leads can be mapped to. Files are matched by header name (`header` or an alias). */
export interface LeadField {
  key: string;
  header: string;
  group: "seller" | "vehicle" | "lead";
  description: string;
  aliases: string[];
  example: string;
}

export interface LeadImportFields {
  fields: LeadField[];
  requirement: string;
  limits: { maxFileBytes: number; maxRows: number; maxApiLeads: number };
  formats: Array<"csv" | "xlsx">;
}

/** File header → lead field key (null = not imported; the value is kept with the lead). */
export type LeadMapping = Record<string, string | null>;

export interface LeadImport {
  id: string;
  storeId: string;
  storeName: string | null;
  kind: "FILE" | "API";
  status: LeadImportStatus;
  defaultSource: OpportunitySource;
  /** The uploaded file (signed, short-lived download URL). */
  file: { name: string | null; contentType: string | null; sizeBytes: number | null; url: string } | null;
  headers: string[];
  mapping: LeadMapping;
  apiKey: { id: string; name: string | null; prefix: string | null } | null;
  counts: { total: number; processed: number; pending: number; created: number; duplicate: number; failed: number; skipped: number };
  /** 0–100. */
  progress: number;
  errorMessage: string | null;
  createdBy: { id: string; name: string | null } | null;
  createdAt: string;
  queuedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

export interface LeadIssue {
  field: string | null;
  message: string;
  level: "error" | "warning";
}

export interface LeadSummary {
  name: string | null;
  phone: string | null;
  email: string | null;
  vehicle: string | null;
  mileage: number | null;
  askingPriceCents: number | null;
  source: OpportunitySource;
  sourceId: string | null;
}

export interface LeadImportPreview {
  mapping: LeadMapping;
  /** Blocking problems (e.g. no Phone or Email column, a field mapped twice). */
  problems: string[];
  /** Up to three example values per column. */
  samples: Record<string, string[]>;
  rows: Array<{ rowNumber: number; lead: LeadSummary | null; issues: LeadIssue[]; blank: boolean }>;
}

export interface LeadImportRow {
  rowNumber: number;
  status: LeadRowStatus;
  lead: LeadSummary | null;
  errors: LeadIssue[];
  opportunityId: string | null;
  processedAt: string | null;
}

export interface ApiKey {
  id: string;
  storeId: string;
  storeName: string | null;
  name: string;
  /** The first characters of the key, to recognize it. */
  prefix: string;
  status: "ACTIVE" | "REVOKED";
  lastUsedAt: string | null;
  createdBy: { id: string; name: string | null } | null;
  createdAt: string;
  revokedAt: string | null;
}

// ── follow-ups ─────────────────────────────────────────────────────────────
/** SELLER_REQUESTED: the seller asked to talk later; RULE: they went quiet (admin rules); MANUAL: the team scheduled it. */
export type FollowupKind = "SELLER_REQUESTED" | "RULE" | "MANUAL";
/** What Rogue does when it's due. MARK_UNRESPONSIVE: the lead is closed as unresponsive (no text). */
export type FollowupAction = "CHECK_IN" | "OFFER_AGAIN" | "RERUN_API_OFFER" | "MARK_UNRESPONSIVE";

export interface Followup {
  id: string;
  opportunityId: string;
  kind: FollowupKind;
  action: FollowupAction;
  status: "SCHEDULED" | "DONE" | "CANCELLED" | "SKIPPED";
  /** Inside the store's texting hours. */
  dueAt: string;
  /** The seller's words, the rule, or the team's note. */
  reason: string;
  rule: { id: string; name: string | null; attempt: number } | null;
  aiRunId: string | null;
  /** Why it ended, or what it did. */
  outcome: string | null;
  createdAt: string;
  completedAt: string | null;
}

/** GET /v1/opportunities/:id/followups */
export interface FollowupList {
  /** The store's time zone: follow-ups are shown and scheduled on its clock. */
  timezone: string;
  next: Followup | null;
  history: Followup[];
}
